import React, { useEffect, useState, useCallback } from 'react';
import { paymentsTrackerAPI } from '../services/api';

const STATUS_CONFIG = {
  PENDING:  { label: 'Pending',  bg: '#fff8e1', color: '#f59e0b', icon: '⏳' },
  PARTIAL:  { label: 'Partial',  bg: '#e8f4fd', color: '#1565c0', icon: '🔵' },
  RECEIVED: { label: 'Received', bg: '#e8f5e9', color: '#2e7d32', icon: '✅' },
  OVERDUE:  { label: 'Overdue',  bg: '#fce4ec', color: '#c62828', icon: '🔴' },
};

const FY_OPTIONS = ['2025-26', '2024-25', '2023-24', '2022-23'];

// Govt TDS rates — fixed as per Income Tax Act (NOT editable by user)
const TDS_SECTIONS = [
  { value: '194J_B',   label: '194J(b) — Professional Services',        rate: 10, desc: 'Most common for creators (sponsored content, influencer marketing)' },
  { value: '194J_A',   label: '194J(a) — Technical Services',           rate: 2,  desc: 'Software, technical consulting' },
  { value: '194C_IND', label: '194C — Contractor (Individual/HUF)',      rate: 1,  desc: 'If brand treats you as a contractor (individual)' },
  { value: '194C_CO',  label: '194C — Contractor (Company/Firm)',        rate: 2,  desc: 'If your creator entity is a company or firm' },
  { value: '194H',     label: '194H — Commission/Brokerage',             rate: 5,  desc: 'Affiliate commissions, referral fees' },
  { value: '194R',     label: '194R — Free Products/Perquisites',        rate: 10, desc: 'Brand gave you free products for promotion' },
  { value: 'NO_PAN',   label: 'No PAN (Sec 206AA)',                      rate: 20, desc: 'TDS is always 20% if you have no PAN — get PAN immediately!' },
];

const emptyForm = {
  brandName: '', description: '', invoiceAmount: '',
  tdsSection: '194J_B',  // default: professional services
  amountReceived: '', paymentDate: '', dueDate: '',
  paymentMode: 'NEFT', utrNumber: '',
  financialYear: '2025-26', form16aReceived: false, notes: '',
};

export default function PaymentsPage() {
  const [payments, setPayments]   = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [showReceiveModal, setShowReceiveModal] = useState(null); // payment object
  const [receiveForm, setReceiveForm] = useState({ amount: '', utr: '', date: '' });

  const load = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        paymentsTrackerAPI.getAll(),
        paymentsTrackerAPI.getSummary(),
      ]);
      setPayments(pRes.data);
      setSummary(sRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const f = (updates) => setForm(p => ({ ...p, ...updates }));

  // Live TDS preview — rate comes from selected section (govt standard)
  const selectedSection = TDS_SECTIONS.find(s => s.value === form.tdsSection) || TDS_SECTIONS[0];
  const tdsRate   = selectedSection.rate;
  const amt       = parseFloat(form.invoiceAmount) || 0;
  const tdsAmt    = parseFloat(((amt * tdsRate) / 100).toFixed(2));
  const netRec    = parseFloat((amt - tdsAmt).toFixed(2));

  const openNew = () => {
    setEditId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      brandName:      p.brandName      || '',
      description:    p.description    || '',
      invoiceAmount:  p.invoiceAmount  || '',
      tdsSection:     p.tdsSection     || '194J_B',
      amountReceived: p.amountReceived || '',
      paymentDate:    p.paymentDate    || '',
      dueDate:        p.dueDate        || '',
      paymentMode:    p.paymentMode    || 'NEFT',
      utrNumber:      p.utrNumber      || '',
      financialYear:  p.financialYear  || '2025-26',
      form16aReceived: p.form16aReceived || false,
      notes:          p.notes          || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.brandName.trim()) { setError('Brand name required'); return; }
    if (!form.invoiceAmount || parseFloat(form.invoiceAmount) <= 0) { setError('Invoice amount required'); return; }
    setSaving(true); setError('');
    try {
      if (editId) await paymentsTrackerAPI.update(editId, form);
      else        await paymentsTrackerAPI.create(form);
      setShowForm(false);
      load();
    } catch (e) { setError(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    await paymentsTrackerAPI.delete(id);
    load();
  };

  const handleMarkReceived = async () => {
    if (!receiveForm.amount || parseFloat(receiveForm.amount) <= 0) return;
    setSaving(true);
    try {
      await paymentsTrackerAPI.markReceived(showReceiveModal.id, receiveForm);
      setShowReceiveModal(null);
      load();
    } catch { alert('Failed to update'); }
    setSaving(false);
  };

  const filtered = filterStatus === 'ALL' ? payments : payments.filter(p => p.status === filterStatus);

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading…</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e' }}>Payment & TDS Tracker 💸</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.875rem' }}>
            Track brand payments, TDS deductions & claim in ITR
          </p>
        </div>
        <button onClick={openNew} style={primaryBtn}>+ Add Payment</button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatCard icon="📋" label={`Total Invoiced (FY ${summary.financialYear})`} value={`₹${fmt(summary.totalInvoiced)}`} color="#1a1a2e" />
          <StatCard icon="✅" label="Total Received" value={`₹${fmt(summary.totalReceived)}`} color="#2e7d32" />
          <StatCard icon="⏳" label="Total Pending" value={`₹${fmt(summary.totalPending)}`} color="#f59e0b" />
          <StatCard icon="🧾" label="TDS Deducted (Claim in ITR)" value={`₹${fmt(summary.totalTds)}`} color="#1565c0" />
          {summary.overdueCount > 0 && (
            <StatCard icon="🔴" label="Overdue Payments" value={summary.overdueCount} color="#c62828" />
          )}
        </div>
      )}

      {/* TDS Info Banner */}
      <div style={{ background: '#e8f4fd', border: '1px solid #bbdefb', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.82rem', color: '#1565c0' }}>
        💡 <b>TDS tip:</b> Brands deduct 10% TDS under <b>Section 194J</b> (professional services). This appears in Form 26AS — you can claim it back in your ITR. Always get <b>Form 16A</b> from the brand.
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {['ALL', 'PENDING', 'PARTIAL', 'RECEIVED', 'OVERDUE'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding: '0.4rem 0.9rem', borderRadius: 20, border: '1.5px solid',
            fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
            borderColor: filterStatus === s ? '#1a7a4a' : '#e0e0e0',
            background: filterStatus === s ? '#1a7a4a' : 'white',
            color: filterStatus === s ? 'white' : '#666',
          }}>
            {s === 'ALL' ? `All (${payments.length})` : `${STATUS_CONFIG[s]?.icon} ${s} (${payments.filter(p => p.status === s).length})`}
          </button>
        ))}
      </div>

      {/* Payment List */}
      {filtered.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: '3rem' }}>💸</div>
          <div style={{ fontWeight: 700, color: '#1a1a2e' }}>{payments.length === 0 ? 'No payments tracked yet' : `No ${filterStatus.toLowerCase()} payments`}</div>
          <div style={{ color: '#888', fontSize: '0.875rem', marginTop: '0.25rem' }}>Add every brand payment to track TDS for ITR filing</div>
          {payments.length === 0 && <button onClick={openNew} style={{ ...primaryBtn, marginTop: '1rem' }}>+ Add First Payment</button>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(p => {
            const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.PENDING;
            const netRcv = parseFloat(p.netReceivable || 0);
            const received = parseFloat(p.amountReceived || 0);
            const pct = netRcv > 0 ? Math.min(100, (received / netRcv) * 100) : 0;

            return (
              <div key={p.id} style={paymentCard}>
                {/* Left */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                    <span style={{ fontWeight: 800, color: '#1a1a2e' }}>{p.brandName}</span>
                    <span style={{ ...badge, background: sc.bg, color: sc.color }}>{sc.icon} {sc.label}</span>
                    {p.financialYear && <span style={{ fontSize: '0.72rem', color: '#aaa' }}>FY {p.financialYear}</span>}
                    {p.form16aReceived && <span style={{ fontSize: '0.72rem', color: '#2e7d32', fontWeight: 700 }}>📄 16A ✓</span>}
                  </div>
                  {p.description && (
                    <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.4rem' }}>
                      {p.description.slice(0, 80)}{p.description.length > 80 ? '…' : ''}
                    </div>
                  )}
                  {/* Amounts row */}
                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8rem', flexWrap: 'wrap' }}>
                    <span><span style={{ color: '#888' }}>Invoice</span> <b>₹{fmt(p.invoiceAmount)}</b></span>
                    <span><span style={{ color: '#888' }}>TDS ({p.tdsRate}%)</span> <b style={{ color: '#1565c0' }}>₹{fmt(p.tdsAmount)}</b></span>
                    <span><span style={{ color: '#888' }}>Net Rcv.</span> <b>₹{fmt(p.netReceivable)}</b></span>
                    <span><span style={{ color: '#888' }}>Received</span> <b style={{ color: '#2e7d32' }}>₹{fmt(p.amountReceived)}</b></span>
                  </div>
                  {/* Progress bar */}
                  {p.status !== 'RECEIVED' && (
                    <div style={{ marginTop: '0.5rem', background: '#f0f0f0', borderRadius: 4, height: 5, width: '100%', maxWidth: 300 }}>
                      <div style={{ height: '100%', borderRadius: 4, background: '#1a7a4a', width: `${pct}%`, transition: 'width 0.3s' }} />
                    </div>
                  )}
                  {p.dueDate && p.status !== 'RECEIVED' && (
                    <div style={{ fontSize: '0.75rem', color: p.status === 'OVERDUE' ? '#c62828' : '#888', marginTop: '0.3rem' }}>
                      {p.status === 'OVERDUE' ? '⚠️ Due was' : '📅 Due:'} {p.dueDate}
                      {p.utrNumber && <span style={{ marginLeft: '1rem' }}>UTR: {p.utrNumber}</span>}
                    </div>
                  )}
                  {p.status === 'RECEIVED' && p.paymentDate && (
                    <div style={{ fontSize: '0.75rem', color: '#2e7d32', marginTop: '0.3rem' }}>
                      ✅ Received on {p.paymentDate}{p.utrNumber && ` · UTR: ${p.utrNumber}`}
                    </div>
                  )}
                </div>

                {/* Right: Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', alignItems: 'flex-end', flexShrink: 0 }}>
                  {p.status !== 'RECEIVED' && (
                    <button onClick={() => { setShowReceiveModal(p); setReceiveForm({ amount: p.netReceivable || '', utr: '', date: new Date().toISOString().slice(0,10) }); }}
                      style={{ ...primaryBtn, fontSize: '0.75rem', padding: '0.4rem 0.8rem' }}>
                      💰 Mark Received
                    </button>
                  )}
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button onClick={() => openEdit(p)} style={iconBtn} title="Edit">✏️</button>
                    <button onClick={() => handleDelete(p.id)} style={{ ...iconBtn, color: '#e53e3e' }} title="Delete">🗑️</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add/Edit Modal ────────────────────────────────────── */}
      {showForm && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#1a1a2e' }}>
                {editId ? 'Edit Payment' : 'Add Payment Record'}
              </h2>
              <button onClick={() => setShowForm(false)} style={closeBtn}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>

              <div style={sectionLabel}>💼 Deal Info</div>

              <label style={labelStyle}>Brand Name *
                <input value={form.brandName} onChange={e => f({ brandName: e.target.value })} style={input} placeholder="e.g. Noise Gadgets" />
              </label>
              <label style={labelStyle}>Financial Year
                <select value={form.financialYear} onChange={e => f({ financialYear: e.target.value })} style={input}>
                  {FY_OPTIONS.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
                </select>
              </label>
              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>Description
                <input value={form.description} onChange={e => f({ description: e.target.value })} style={input}
                  placeholder="e.g. YouTube integration - Diwali campaign Oct 2024" />
              </label>

              <div style={sectionLabel}>💰 Amount & TDS</div>

              <label style={labelStyle}>Invoice Amount (₹) *
                <input value={form.invoiceAmount} onChange={e => f({ invoiceAmount: e.target.value })} style={input} type="number" placeholder="50000" />
              </label>
              <label style={labelStyle}>Amount Received so far (₹)
                <input value={form.amountReceived} onChange={e => f({ amountReceived: e.target.value })} style={input} type="number" placeholder="0" />
              </label>

              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
                TDS Section (as per Income Tax Act)
                <select value={form.tdsSection} onChange={e => f({ tdsSection: e.target.value })} style={input}>
                  {TDS_SECTIONS.map(s => (
                    <option key={s.value} value={s.value}>
                      {s.label} — {s.rate}%
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.2rem' }}>
                  {selectedSection.desc}
                </span>
              </label>

              {/* Live TDS Preview — read-only, auto-calculated */}
              {amt > 0 && (
                <div style={{ gridColumn: '1/-1', background: '#e8f4fd', border: '1px solid #bbdefb', borderRadius: 10, padding: '0.85rem', fontSize: '0.85rem' }}>
                  <div style={{ fontWeight: 700, color: '#1565c0', marginBottom: '0.5rem' }}>
                    TDS Calculation — {selectedSection.label} @ {tdsRate}%
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                    <div><span style={{ color: '#888' }}>Invoice Amount</span><br /><b>₹{amt.toLocaleString('en-IN')}</b></div>
                    <div><span style={{ color: '#888' }}>TDS Deducted ({tdsRate}%)</span><br /><b style={{ color: '#c62828' }}>- ₹{tdsAmt.toLocaleString('en-IN')}</b></div>
                    <div><span style={{ color: '#888' }}>You Receive</span><br /><b style={{ color: '#2e7d32', fontSize: '1rem' }}>₹{netRec.toLocaleString('en-IN')}</b></div>
                  </div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#1565c0' }}>
                    ℹ️ ₹{tdsAmt.toLocaleString('en-IN')} TDS will appear in your Form 26AS — claim it in ITR to get refund
                  </div>
                  {form.tdsSection === 'NO_PAN' && (
                    <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: '#c62828', fontWeight: 700 }}>
                      ⚠️ 20% TDS because no PAN — apply for PAN immediately to reduce to 10%!
                    </div>
                  )}
                </div>
              )}

              <div style={sectionLabel}>📅 Payment Dates</div>

              <label style={labelStyle}>Due Date
                <input value={form.dueDate} onChange={e => f({ dueDate: e.target.value })} style={input} type="date" />
              </label>
              <label style={labelStyle}>Payment Received Date
                <input value={form.paymentDate} onChange={e => f({ paymentDate: e.target.value })} style={input} type="date" />
              </label>
              <label style={labelStyle}>Payment Mode
                <select value={form.paymentMode} onChange={e => f({ paymentMode: e.target.value })} style={input}>
                  {['NEFT', 'RTGS', 'UPI', 'Cheque', 'Cash', 'Wire Transfer'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label style={labelStyle}>UTR / Transaction Ref
                <input value={form.utrNumber} onChange={e => f({ utrNumber: e.target.value })} style={input} placeholder="UTR number" />
              </label>

              <label style={{ ...labelStyle, gridColumn: '1/-1', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                <input type="checkbox" checked={form.form16aReceived} onChange={e => f({ form16aReceived: e.target.checked })} />
                <span>Received Form 16A from brand (TDS certificate)</span>
              </label>

              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>Notes
                <textarea value={form.notes} onChange={e => f({ notes: e.target.value })} style={{ ...input, height: 55, resize: 'vertical' }}
                  placeholder="Any notes about this payment..." />
              </label>
            </div>

            {error && <div style={{ color: '#e53e3e', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</div>}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={outlineBtn}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={primaryBtn}>
                {saving ? 'Saving…' : editId ? 'Update' : 'Add Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mark Received Modal ───────────────────────────────── */}
      {showReceiveModal && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#1a1a2e' }}>💰 Mark Payment Received</h2>
              <button onClick={() => setShowReceiveModal(null)} style={closeBtn}>✕</button>
            </div>
            <div style={{ background: '#f0f9f4', borderRadius: 10, padding: '0.85rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
              <b>{showReceiveModal.brandName}</b><br />
              Net Receivable: <b style={{ color: '#1a7a4a' }}>₹{fmt(showReceiveModal.netReceivable)}</b>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <label style={labelStyle}>Amount Received (₹) *
                <input value={receiveForm.amount} onChange={e => setReceiveForm(r => ({ ...r, amount: e.target.value }))}
                  style={input} type="number" placeholder={showReceiveModal.netReceivable} />
              </label>
              <label style={labelStyle}>Date Received
                <input value={receiveForm.date} onChange={e => setReceiveForm(r => ({ ...r, date: e.target.value }))}
                  style={input} type="date" />
              </label>
              <label style={labelStyle}>UTR / Transaction Ref
                <input value={receiveForm.utr} onChange={e => setReceiveForm(r => ({ ...r, utr: e.target.value }))}
                  style={input} placeholder="UTR number for records" />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowReceiveModal(null)} style={outlineBtn}>Cancel</button>
              <button onClick={handleMarkReceived} disabled={saving} style={primaryBtn}>
                {saving ? 'Saving…' : '✅ Confirm Received'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-components
function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ background: 'white', borderRadius: 14, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: '1.3rem' }}>{icon}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color, marginTop: '0.2rem' }}>{value}</div>
      <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.1rem' }}>{label}</div>
    </div>
  );
}

function fmt(val) {
  const n = parseFloat(val || 0);
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Styles
const primaryBtn = { background: '#1a7a4a', color: 'white', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const outlineBtn = { background: 'white', color: '#1a7a4a', border: '1.5px solid #1a7a4a', borderRadius: 10, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const iconBtn    = { background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.55rem', cursor: 'pointer', fontSize: '0.9rem' };
const badge      = { padding: '0.18rem 0.6rem', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 };
const input      = { padding: '0.55rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'white' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: '#444' };
const sectionLabel = { gridColumn: '1/-1', fontWeight: 800, fontSize: '0.78rem', color: '#1a7a4a', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '2px solid #e8f5e9' };
const paymentCard = { background: 'white', borderRadius: 14, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' };
const emptyState = { textAlign: 'center', padding: '4rem 2rem', background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const overlay    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' };
const modal      = { background: 'white', borderRadius: 18, padding: '1.75rem', width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const closeBtn   = { background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '1rem' };
